import { AxiosError } from 'axios';

import { IControllerResponse } from './types';

/**
 * Pulls a human-friendly string out of an error returned by a hsm-backend
 * call. The backend error envelope carries the human text in `description`
 * (`message` is only kept as a fallback for any caller that still sets it);
 * falls back to the timeout-specific message and lastly to the generic Error
 * message. Designed to be safe to call from a toast handler — it never throws.
 */
export function extractBackendErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const payload = error.response?.data as
      | IControllerResponse<unknown>
      | undefined;

    const backendText = payload?.message ?? payload?.description;

    if (backendText) {
      return backendText;
    }
    if (error.code === 'ECONNABORTED') {
      return 'El backend tardó demasiado en responder.';
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Error desconocido';
}

/**
 * Returns the HTTP status of a hsm-backend error, or `undefined` if the error
 * is not an Axios response error. Lets callers branch on 409 / 422 / etc.
 */
export function getBackendErrorStatus(error: unknown): number | undefined {
  return error instanceof AxiosError ? error.response?.status : undefined;
}

/**
 * Returns the backend error `code` (e.g. 'LEGACY_SUBSCRIPTION_ACTIVE') from the
 * `IControllerResponse` envelope, or `undefined`. Lets callers disambiguate
 * errors that share an HTTP status (e.g. two distinct 409s).
 */
export function getBackendErrorCode(error: unknown): string | undefined {
  if (error instanceof AxiosError) {
    const payload = error.response?.data as
      | IControllerResponse<unknown>
      | undefined;

    return payload?.code;
  }
  return undefined;
}
